import React, { useMemo, useState } from 'react';
import { useReveal } from '../lib/useReveal';
import { cn } from '../lib/cn';

const SAVINGS_PER_M2 = { single: 150, oldDouble: 70 };
const EUR_PER_KWH = 0.1;

const formatNumber = (n, locale) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(n));

export const Calculator = ({ data, language = 'bg', onRequestQuote }) => {
  const headRef = useReveal();
  const bodyRef = useReveal();
  const locale = language === 'bg' ? 'bg-BG' : 'en-US';

  const [area, setArea] = useState(20);
  const [existing, setExisting] = useState('single');

  const { energy, money } = useMemo(() => {
    const kwh = area * SAVINGS_PER_M2[existing];
    return { energy: kwh, money: kwh * EUR_PER_KWH };
  }, [area, existing]);

  if (!data) return null;

  return (
    <section
      id="kalkulator"
      className="section-pad bg-paper text-ink relative overflow-hidden"
    >
      <div className="max-wrap section-wrap">
        <div ref={headRef} className="reveal grid grid-cols-12 gap-y-6 gap-x-6 items-end">
          <div className="col-span-12 lg:col-span-3">
            <span className="font-mono text-[0.68rem] uppercase tracking-[0.22em] text-ink-muted">
              {data.eyebrow}
            </span>
            <span className="block mt-4 h-px w-16 bg-ink/60" aria-hidden="true" />
          </div>
          <div className="col-span-12 lg:col-span-9">
            <h2
              className="font-serif text-balance tracking-tightest leading-[0.98]"
              style={{ fontSize: 'clamp(2.25rem, 8vw, 6rem)' }}
            >
              {data.title}
            </h2>
            {data.desc && (
              <p className="mt-8 max-w-2xl text-ink-muted text-base sm:text-lg leading-relaxed text-pretty">
                {data.desc}
              </p>
            )}
          </div>
        </div>

        <div
          ref={bodyRef}
          className="reveal mt-14 sm:mt-20 grid grid-cols-12 gap-y-14 gap-x-6 md:gap-x-10 lg:gap-x-16 items-stretch"
        >
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-12">
            <div>
              <div className="flex items-baseline justify-between mb-6">
                <label
                  htmlFor="calc-area"
                  className="font-mono text-[0.68rem] uppercase tracking-[0.22em] text-ink-muted"
                >
                  {data.areaLabel}
                </label>
                <span className="font-serif tracking-editorial text-ink leading-none text-2xl sm:text-3xl tabular-nums">
                  {area}
                  <span className="ml-1 text-ink-muted text-sm font-sans">{data.areaUnit}</span>
                </span>
              </div>
              <input
                id="calc-area"
                type="range"
                min={5}
                max={100}
                step={1}
                value={area}
                onChange={(e) => setArea(Number(e.target.value))}
                className="calc-range w-full"
                aria-valuemin={5}
                aria-valuemax={100}
                aria-valuenow={area}
              />
              <div className="mt-2 flex justify-between font-mono text-[0.65rem] tabular-nums text-ink-faint">
                <span>5</span>
                <span>100</span>
              </div>
            </div>

            <div>
              <span className="block font-mono text-[0.68rem] uppercase tracking-[0.22em] text-ink-muted mb-4">
                {data.existingLabel}
              </span>
              <div className="grid grid-cols-2 gap-0 border border-rule">
                <ExistingOption
                  active={existing === 'single'}
                  onClick={() => setExisting('single')}
                >
                  {data.existingSingle}
                </ExistingOption>
                <ExistingOption
                  active={existing === 'oldDouble'}
                  onClick={() => setExisting('oldDouble')}
                  leftBorder
                >
                  {data.existingOldDouble}
                </ExistingOption>
              </div>
            </div>

            {data.disclaimer && (
              <p className="text-xs leading-relaxed text-ink-faint max-w-sm">{data.disclaimer}</p>
            )}
          </div>

          <div className="col-span-12 lg:col-span-7 lg:pl-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-rule border border-rule h-full">
              <ResultCard
                eyebrow={data.resultEnergy}
                value={formatNumber(energy, locale)}
                unit={data.unitEnergy}
              />
              <ResultCard
                eyebrow={data.resultMoney}
                value={formatNumber(money, locale)}
                unit={data.unitMoney}
                accent
              />
              <div className="col-span-1 sm:col-span-2 bg-paper-soft p-8 sm:p-10 flex items-center justify-between gap-6 flex-wrap">
                <span className="font-mono text-[0.68rem] uppercase tracking-[0.22em] text-ink-muted">
                  {language === 'bg' ? 'Готови за стъпка напред?' : 'Ready for the next step?'}
                </span>
                <button
                  type="button"
                  onClick={onRequestQuote}
                  className="group inline-flex items-center gap-4 cursor-pointer text-ink"
                >
                  <span className="relative font-sans text-sm tracking-[0.18em] uppercase">
                    {data.cta}
                    <span className="absolute left-0 -bottom-1 w-full h-px bg-ink transition-transform duration-500 ease-out-quart origin-right scale-x-100 group-hover:origin-left" />
                  </span>
                  <svg viewBox="0 0 24 24" fill="none" className="w-10 h-px overflow-visible text-ink transition-transform duration-500 ease-out-quart group-hover:translate-x-2" aria-hidden="true">
                    <path stroke="currentColor" strokeWidth="1" strokeLinecap="square" d="M0 12h24m0 0l-6-6m6 6l-6 6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ExistingOption = ({ active, onClick, leftBorder, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn(
      'px-5 py-5 text-left font-serif tracking-editorial text-ink text-base sm:text-lg leading-snug transition-colors duration-300',
      leftBorder && 'border-l border-rule',
      active ? 'bg-ink text-paper' : 'bg-transparent hover:bg-paper-soft'
    )}
  >
    {children}
  </button>
);

const ResultCard = ({ eyebrow, value, unit, accent }) => (
  <div className={cn('p-8 sm:p-12', accent ? 'bg-ink text-paper' : 'bg-paper')}>
    <span
      className={cn(
        'font-mono text-[0.68rem] uppercase tracking-[0.22em]',
        accent ? 'text-paper/60' : 'text-ink-muted'
      )}
    >
      {eyebrow}
    </span>
    <div className="mt-6 flex items-baseline gap-3 flex-wrap">
      <span
        className={cn(
          'font-serif tracking-tightest leading-none tabular-nums',
          accent ? 'text-paper' : 'text-ink'
        )}
        style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}
      >
        {value}
      </span>
      <span
        className={cn(
          'font-sans text-sm sm:text-base',
          accent ? 'text-paper/70' : 'text-ink-muted'
        )}
      >
        {unit}
      </span>
    </div>
  </div>
);
